import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FaClock, FaSignOutAlt, FaSignInAlt, FaCalendarCheck, FaUser,
  FaHourglassHalf, FaPlaneDeparture, FaExclamationTriangle,
} from 'react-icons/fa';
import api, { errorMessage, getUser } from '../api';
import {
  PageShell, Card, StatCard, Badge, Button, Alert, Loading, EmptyState,
  formatTime, formatDate, todayISO,
} from './attendance/ui';

/** The signed-in staff member's own attendance: punch clock, month summary,
 *  leave balances and recent history. */
const AttendanceDashboard = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [balances, setBalances] = useState([]);
  const [user] = useState(getUser());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const now = new Date();
      const [historyRes, calendarRes, balanceRes] = await Promise.all([
        api.get('attendance/', { params: { personal: 'true' } }),
        api.get('attendance/calendar/', {
          params: { month: now.getMonth() + 1, year: now.getFullYear() },
        }),
        api.get('leave-balances/mine/'),
      ]);

      setHistory(historyRes.data);
      setSummary(calendarRes.data.summary);
      setBalances(balanceRes.data);
      setToday(historyRes.data.find((record) => record.date === todayISO()) || null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load your attendance.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    load();
    return () => clearInterval(timer);
  }, [load]);

  const punch = async (endpoint) => {
    setBusy(true);
    setError('');
    try {
      await api.post(`attendance/${endpoint}/`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not record your punch.'));
    } finally {
      setBusy(false);
    }
  };

  const duration = (record) => {
    if (record?.workingHours) return `${record.workingHours}h`;
    if (!record?.checkIn || !record?.checkOut) return '—';
    const minutes = Math.round((new Date(record.checkOut) - new Date(record.checkIn)) / 60000);
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };

  if (loading) {
    return <PageShell title="My Attendance"><Loading /></PageShell>;
  }

  const state = today?.checkIn && !today?.checkOut ? 'in'
    : today?.checkOut ? 'done' : 'out';

  return (
    <PageShell
      title="My Attendance"
      subtitle={currentTime.toLocaleDateString('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      })}
      actions={<Link to="/leave-management"><Button variant="secondary"><FaPlaneDeparture /> Request leave</Button></Link>}
    >
      <Alert>{error}</Alert>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Punch clock */}
        <Card className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#15A857]/10 flex items-center justify-center">
                <FaUser className="text-[#15A857] text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">{user?.name || user?.username}</h2>
                <p className="text-gray-500 text-sm">
                  {user?.employeeId}{user?.department ? ` · ${user.department}` : ''}
                </p>
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-800 tabular-nums">
                {currentTime.toLocaleTimeString('en-GB')}
              </div>
              <Badge value={state === 'in' ? 'present' : state === 'done' ? 'holiday' : 'no_record'}
                label={state === 'in' ? 'Checked in' : state === 'done' ? 'Checked out' : 'Not checked in'} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                <FaSignInAlt className="text-green-600" /> Check in
              </div>
              <div className="text-2xl font-bold text-green-700">{formatTime(today?.checkIn)}</div>
              {today?.isLate && (
                <div className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                  <FaExclamationTriangle /> {today.lateMinutes} min late
                </div>
              )}
            </div>
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                <FaSignOutAlt className="text-blue-600" /> Check out
              </div>
              <div className="text-2xl font-bold text-blue-700">{formatTime(today?.checkOut)}</div>
            </div>
            <div className="bg-purple-50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-gray-600 text-sm mb-1">
                <FaClock className="text-purple-600" /> Hours today
              </div>
              <div className="text-2xl font-bold text-purple-700">{duration(today)}</div>
              {today?.overtimeHours > 0 && (
                <div className="text-xs text-purple-600 mt-1">+{today.overtimeHours}h overtime</div>
              )}
            </div>
          </div>

          {state === 'out' && (
            <Button onClick={() => punch('check-in')} disabled={busy} className="w-full py-4 text-lg">
              <FaSignInAlt /> Check In
            </Button>
          )}
          {state === 'in' && (
            <Button onClick={() => punch('check-out')} disabled={busy}
              className="w-full py-4 text-lg bg-blue-600 hover:bg-blue-700 shadow-blue-600/20">
              <FaSignOutAlt /> Check Out
            </Button>
          )}
          {state === 'done' && (
            <div className="w-full bg-gray-100 text-gray-600 py-4 rounded-xl font-semibold text-center flex items-center justify-center gap-2">
              <FaCalendarCheck /> Attendance complete for today
            </div>
          )}
        </Card>

        {/* Leave balances */}
        <Card title="Leave balance">
          {balances.length === 0 ? (
            <EmptyState title="No leave types configured" hint="HR sets these up in system settings." />
          ) : (
            <div className="space-y-3">
              {balances.map((balance) => (
                <div key={balance.id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{balance.leaveType}</span>
                  <span className="text-sm font-semibold text-gray-800">
                    {Number(balance.remainingDays)}
                    <span className="text-gray-400 font-normal">
                      {' / '}{Number(balance.entitledDays) + Number(balance.carriedForwardDays)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
          <Link to="/leave-management" className="block mt-5">
            <Button variant="secondary" className="w-full">Manage leave</Button>
          </Link>
        </Card>
      </div>

      {/* This month */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <StatCard icon={<FaCalendarCheck />} label="Days present" tone="green"
            value={summary.daysPresent} hint={`of ${summary.totalWorkingDays} working days`} />
          <StatCard icon={<FaExclamationTriangle />} label="Late days" tone="amber"
            value={summary.lateDays} hint={`${summary.lateMinutes} min total`} />
          <StatCard icon={<FaUser />} label="Days absent" tone="red" value={summary.daysAbsent} />
          <StatCard icon={<FaPlaneDeparture />} label="Leave days" tone="purple" value={summary.leaveDays} />
          <StatCard icon={<FaHourglassHalf />} label="Hours worked" tone="blue"
            value={summary.totalWorkingHours} hint={`+${summary.overtimeHours}h overtime`} />
        </div>
      )}

      <Card
        title="Recent attendance"
        action={<Link to="/monthly-report" className="text-sm font-semibold text-[#15A857] hover:underline">Full report</Link>}
      >
        {history.length === 0 ? (
          <EmptyState title="No attendance records yet" hint="Check in to start tracking." />
        ) : (
          <div className="space-y-3">
            {history.slice(0, 10).map((record) => (
              <div key={record.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm">
                    <FaCalendarCheck className="text-gray-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-800">{formatDate(record.date)}</div>
                    <div className="text-sm text-gray-500">
                      {formatTime(record.checkIn)} – {formatTime(record.checkOut)}
                      {record.lateMinutes > 0 && ` · ${record.lateMinutes} min late`}
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <div className="text-sm font-semibold text-gray-800">{duration(record)}</div>
                  <Badge value={record.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </PageShell>
  );
};

export default AttendanceDashboard;
